import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, MinusCircle, Send, UserPlus } from 'lucide-react';
import {
  AddButton, Drawer, Field, LiveScreen, QueryState, Select, StatusPill,
  formatDate, type Column,
} from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { callRpc } from '@/api/resources';
import { getSupabase } from '@/lib/supabase';
import { useAuth } from '@/auth/AuthProvider';
import { can, isAdmin, normaliseIdentifier } from '@/auth/session';
import { grantableRoles } from '@/lib/accessTabs';
import { toast } from '@/lib/toast';

interface OnboardingRow {
  id: string;
  request_code: string | null;
  user_id: string | null;
  invited_name: string | null;
  invited_email: string | null;
  invited_mobile: string | null;
  requested_user_type: string;
  invitation_channel: string;
  invited_at: string;
  expires_at: string;
  accepted_at: string | null;
  activation_status: string;
  party_link_status: string;
  role_assignment_status: string;
  scope_assignment_status: string;
  mfa_enrollment_status: string;
  device_binding_status: string;
  kyc_status: string;
  consent_status: string;
  steps_complete: number | null;
  steps_total: number | null;
}

interface RoleRow { id: string; name: string; tier: number }

const GATES: Array<{ key: keyof OnboardingRow; label: string }> = [
  { key: 'party_link_status', label: 'Linked to a party' },
  { key: 'role_assignment_status', label: 'Role assigned' },
  { key: 'scope_assignment_status', label: 'Places assigned' },
  { key: 'mfa_enrollment_status', label: 'Second factor' },
  { key: 'device_binding_status', label: 'Device bound' },
  { key: 'kyc_status', label: 'KYC' },
  { key: 'consent_status', label: 'Consent' },
];

/**
 * Onboarding: turning an invitation into somebody who can sign in.
 *
 * The seven gates are not decoration - `authz.user_onboarding_requests` carries one column per
 * gate, and they are how an admin can see WHY a person cannot work yet. A staff member needs a
 * role and some places; a farmer-facing account also needs KYC and consent. Marking a gate
 * `not_required` is a deliberate statement, not a way of skipping it.
 *
 * Activation is the last step and is not done here: it calls the `provision-user` function,
 * which creates the auth account and links it in one place, so auth.users and people.users
 * cannot drift apart.
 */
export default function OnboardingScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [open, setOpen] = useState<OnboardingRow | null>(null);
  const [stat, setStat] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [form, setForm] = useState({ full_name: '', role_id: '', mobile: '', whatsapp_number: '', email: '' });

  const requests = useRows<OnboardingRow>('user_onboarding_requests', {
    orderBy: { column: 'invited_at', ascending: false },
  });
  const roles = useRows<RoleRow>('roles', { orderBy: { column: 'tier' } });
  const admin = isAdmin(profile);
  const mayInvite = admin && can(profile, 'user.onboarding', 'create');

  // Only the roles this person could actually be granted: the server refuses the rest, and a
  // picker that offers them is promising something it cannot deliver.
  const roleOptions = useMemo(
    () => grantableRoles(profile, roles.data ?? [])
      .map((role) => ({ id: role.id, label: `${role.name} (tier ${role.tier})` })),
    [roles.data, profile],
  );

  // Rejected and cancelled share a card: both are an invitation someone closed on purpose,
  // unlike Expired, which nobody acted on.
  const STATS = useMemo(() => [
    { key: 'all', label: 'Invitations', match: () => true, tone: 'neutral' as const },
    { key: 'draft', label: 'Draft', tone: 'info' as const, match: (row: OnboardingRow) => row.activation_status === 'draft' },
    { key: 'invited', label: 'Invited', tone: 'warn' as const, match: (row: OnboardingRow) => row.activation_status === 'invited' },
    { key: 'pending_approval', label: 'Pending approval', tone: 'warn' as const,
      match: (row: OnboardingRow) => row.activation_status === 'pending_approval' },
    { key: 'active', label: 'Active', tone: 'ok' as const, match: (row: OnboardingRow) => row.activation_status === 'active' },
    { key: 'expired', label: 'Expired', tone: 'info' as const, match: (row: OnboardingRow) => row.activation_status === 'expired' },
    { key: 'closed', label: 'Rejected or cancelled', tone: 'error' as const,
      match: (row: OnboardingRow) => row.activation_status === 'rejected' || row.activation_status === 'cancelled' },
  ], []);

  const visible = useMemo(() => applyStat(requests.data ?? [], STATS, stat), [requests.data, STATS, stat]);

  const columns: Array<Column<OnboardingRow>> = [
    { key: 'invited_name', label: 'Person', render: (row) => row.invited_name ?? '--' },
    {
      key: 'contact', label: 'Invited on',
      render: (row) => row.invited_email ?? row.invited_mobile ?? '--',
    },
    { key: 'requested_user_type', label: 'Type' },
    { key: 'invitation_channel', label: 'Channel' },
    {
      key: 'steps', label: 'Steps', align: 'right',
      render: (row) => `${row.steps_complete ?? 0} of ${row.steps_total ?? GATES.length}`,
    },
    { key: 'expires_at', label: 'Invitation expires', render: (row) => formatDate(row.expires_at) },
    { key: 'activation_status', label: 'Status', render: (row) => <StatusPill value={row.activation_status} /> },
  ];

  async function invite() {
    setError(undefined);
    if (!form.full_name.trim()) { setError('A name is required.'); return; }
    if (!form.role_id) { setError('Choose a role. Someone with no role can sign in and see nothing.'); return; }
    if (!form.mobile.trim() || !form.whatsapp_number.trim() || !form.email.trim()) {
      setError('A mobile number, a WhatsApp number and an email address are all required: each carries messages the others do not.');
      return;
    }
    for (const [key, channel] of [['mobile', 'sms'], ['whatsapp_number', 'whatsapp'], ['email', 'email']] as const) {
      const raw = form[key].trim();
      const result = normaliseIdentifier(channel, raw);
      if ('error' in result) { setError(result.error); return; }
    }

    setBusy(true);
    try {
      await callRpc('fn_invite_user', {
        p_full_name: form.full_name.trim(),
        p_role_id: form.role_id,
        p_mobile: tidy(form.mobile, 'sms'),
        p_whatsapp_number: tidy(form.whatsapp_number, 'whatsapp'),
        p_email: tidy(form.email, 'email'),
        p_invitation_channel: form.whatsapp_number ? 'whatsapp' : form.email ? 'email' : 'sms',
      });
      await queryClient.invalidateQueries({ queryKey: ['user_onboarding_requests'] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Invited. Activate their login when they are ready to start.');
      setInviting(false);
      setForm({ full_name: '', role_id: '', mobile: '', whatsapp_number: '', email: '' });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The invitation could not be created.');
    } finally {
      setBusy(false);
    }
  }

  async function activate(row: OnboardingRow) {
    if (!row.user_id) { toast.error('This invitation has no user record.'); return; }
    setBusy(true);
    try {
      const { data, error: invokeError } = await getSupabase().functions.invoke('provision-user', {
        body: { user_id: row.user_id },
      });
      if (invokeError) throw invokeError;
      const payload = data as { error?: string; sign_in_with?: string };
      if (payload?.error) throw new Error(payload.error);
      await queryClient.invalidateQueries({ queryKey: ['user_onboarding_requests'] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`${row.invited_name} can now sign in by ${payload.sign_in_with ?? 'code'}.`);
      setOpen(null);
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'The login could not be created.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <LiveScreen
      actions={<AddButton disabled={!mayInvite} label="Invite someone" onClick={() => { setError(undefined); setInviting(true); }}
                          title={mayInvite ? undefined : 'Only an organisation admin may invite.'} />}
      subtitle="Nobody can create their own account. Everyone starts as an invitation here."
      title="Onboarding"
    >
      <StatCards active={stat} onSelect={setStat} rows={requests.data ?? []} stats={STATS} />

      <QueryState empty="Nobody has been invited yet." query={requests}>
        <DataGrid exportName={'onboarding-requests'} recordNoun={'request'} searchPlaceholder={'Name, mobile or email'} deleteView={'user_onboarding_requests'} screenKey={'user.onboarding'} columns={columns} onRowClick={setOpen} rows={visible} selectedId={open?.id} />
      </QueryState>

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setInviting(false)} type="button">Cancel</button>
            <button className="primary" disabled={busy} onClick={() => void invite()} type="button">
              <UserPlus size={15} /> {busy ? 'Inviting…' : 'Invite'}
            </button>
          </>
        }
        onClose={() => setInviting(false)}
        open={inviting}
        subtitle="They will receive a one-time code. There is no password to set."
        title="Invite someone"
      >
        <Field label="Full name" required>
          <input onChange={(event) => setForm({ ...form, full_name: event.target.value })} value={form.full_name} />
        </Field>
        <Field hint="You cannot grant more authority than you hold yourself." label="Role" required>
          <Select onChange={(value) => setForm({ ...form, role_id: value })} options={roleOptions} value={form.role_id} />
        </Field>
        <div className="field-row">
          <Field label="Mobile" required>
            <input inputMode="tel" onChange={(event) => setForm({ ...form, mobile: event.target.value })} value={form.mobile} />
          </Field>
          <Field label="WhatsApp number" required>
            <input inputMode="tel" onChange={(event) => setForm({ ...form, whatsapp_number: event.target.value })}
                   value={form.whatsapp_number} />
          </Field>
        </div>
        <Field hint="All three are required: each carries messages the others do not." label="Email" required>
          <input inputMode="email" onChange={(event) => setForm({ ...form, email: event.target.value })} value={form.email} />
        </Field>
        {error && <p className="login-error" role="alert">{error}</p>}
      </Drawer>

      <Drawer
        footer={
          <>
            <button onClick={() => setOpen(null)} type="button">Close</button>
            {open?.activation_status !== 'active' && (
              <button className="primary" disabled={busy || !admin} onClick={() => open && void activate(open)}
                      title={admin ? undefined : 'Only an organisation admin may activate a login.'} type="button">
                <Send size={15} /> {busy ? 'Activating…' : 'Activate login'}
              </button>
            )}
          </>
        }
        onClose={() => setOpen(null)}
        open={Boolean(open)}
        subtitle={open?.request_code ?? undefined}
        title={open?.invited_name ?? 'Invitation'}
      >
        {open && (
          <>
            <dl className="detail-grid">
              <div><dt>Type</dt><dd>{open.requested_user_type}</dd></div>
              <div><dt>Invited on</dt><dd>{open.invited_email ?? open.invited_mobile ?? '--'}</dd></div>
              <div><dt>Channel</dt><dd>{open.invitation_channel}</dd></div>
              <div><dt>Invited</dt><dd>{formatDate(open.invited_at)}</dd></div>
              <div><dt>Expires</dt><dd>{formatDate(open.expires_at)}</dd></div>
              <div><dt>Status</dt><dd><StatusPill value={open.activation_status} /></dd></div>
            </dl>

            <div className="section-heading"><h3>Before they can work</h3></div>
            <ul className="gate-list">
              {GATES.map((gate) => {
                const value = String(open[gate.key] ?? 'pending');
                const done = value === 'complete' || value === 'approved';
                const skipped = value === 'not_required';
                return (
                  <li key={gate.key}>
                    {done ? <CheckCircle2 className="gate-done" size={15} />
                          : skipped ? <MinusCircle className="gate-skip" size={15} />
                          : <Circle className="gate-open" size={15} />}
                    <span>{gate.label}</span>
                    <em>{skipped ? 'not required' : value.replace(/_/g, ' ')}</em>
                  </li>
                );
              })}
            </ul>
            <p className="muted" style={{ margin: 0, fontSize: '12.5px' }}>
              Activating creates their login against the contact recorded here - never one typed
              in at the time - so a code can only ever reach the person the admin invited.
            </p>
          </>
        )}
      </Drawer>
    </LiveScreen>
  );
}

function tidy(raw: string, channel: 'sms' | 'whatsapp' | 'email'): string | null {
  const value = raw.trim();
  if (!value) return null;
  const result = normaliseIdentifier(channel, value);
  return 'value' in result ? result.value : value;
}
